import React, { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  ClosedPositionData,
  PositionData,
} from "@crypto-trading/common/browser";
import { Dialog } from "@/components/Dialog";
import { TabSelector } from "@/components/TabSelector";
import { useCopyTradeMirroredPositions } from "@/hooks/useCopyTrade";
import {
  useTokenLivePrice,
  type LivePriceExchangeCode,
} from "@/hooks/useTokenLivePrice";
import type { CopyTradeMirroredPositionView } from "@/api/copy-trade";

interface CopyTradeMirroredPositionsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subscriptionId: string;
  userId: string;
  nickname: string;
  onSelectPosition: (position: PositionData | ClosedPositionData) => void;
}

type PositionsTab = "open" | "closed";

const isFiniteNumber = (value: number | null | undefined): value is number =>
  value != null && Number.isFinite(value);

const formatUsd = (value: number | null | undefined) =>
  isFiniteNumber(value)
    ? value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "—";

const formatSignedUsd = (value: number | null | undefined) => {
  if (!isFiniteNumber(value)) return "—";
  const prefix = value >= 0 ? "+" : "";
  return `${prefix}$${formatUsd(value)}`;
};

const formatDecimal = (value: number | null | undefined, decimals = 4) =>
  isFiniteNumber(value) ? value.toFixed(decimals) : "—";

const formatDateTime = (iso?: string | null) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const pnlColor = (value: number | null | undefined) => {
  if (!isFiniteNumber(value) || value === 0) return "text-text-primary";
  return value > 0 ? "text-green-400" : "text-red-400";
};

const mapExchangeCode = (
  exchangeCode?: string | null,
  exchangeName?: string | null
): LivePriceExchangeCode => {
  const code = (exchangeCode || exchangeName || "").toLowerCase();
  if (code.includes("binance")) return "binance";
  if (code.includes("bybit")) return "bybit";
  if (code.includes("gate")) return "gate";
  if (code.includes("kucoin")) return "kucoin";
  if (code.includes("aster")) return "asterdex";
  return "binance";
};

const calculateUnrealizedPnL = (
  entryPrice: number,
  markPrice: number,
  usdAmount: number,
  leverage: number,
  positionType: string
): { pnl: number; pnlPercentage: number } => {
  if (!entryPrice || !markPrice || entryPrice === 0) {
    return { pnl: 0, pnlPercentage: 0 };
  }
  const isLong = positionType.toUpperCase() === "LONG";
  const priceChange = isLong
    ? (markPrice - entryPrice) / entryPrice
    : (entryPrice - markPrice) / entryPrice;
  return {
    pnl: usdAmount * leverage * priceChange,
    pnlPercentage: priceChange * leverage * 100,
  };
};

export function mirroredViewToDrawerPosition(
  position: CopyTradeMirroredPositionView
): PositionData | ClosedPositionData {
  if (position.status === "CLOSED") {
    return {
      id: position.tradingPositionId,
      symbol: position.symbol,
      positionType: position.positionType,
      amount: position.amount,
      usdAmount: position.usdAmount,
      leverage: position.leverage,
      entryPrice: position.entryPrice,
      exitPrice: position.exitPrice ?? null,
      realizedPnl: position.realizedPnl ?? null,
      pnlPercentage: position.pnlPercentage ?? null,
      openedAt: position.openedAt,
      closedAt: position.closedAt ?? null,
      exchangeCode: position.exchangeCode ?? undefined,
      exchangeName: position.exchangeName ?? undefined,
      tradeOrigin: "COPY_TRADE",
      alertId: null,
    };
  }

  const positionId =
    position.exchangePositionId || `db#${position.tradingPositionId}`;

  return {
    positionId,
    symbol: position.symbol,
    positionType: position.positionType,
    amount: String(position.amount),
    usdAmount: String(position.usdAmount),
    leverage: String(position.leverage),
    entryPrice: String(position.entryPrice),
    markPrice: String(position.markPrice ?? position.entryPrice),
    exitPrice: "",
    pnl: String(position.unrealizedPnl ?? 0),
    pnlPercentage: String(position.pnlPercentage ?? 0),
    openedAt: position.openedAt,
    exchangeCode: position.exchangeCode ?? undefined,
    exchangeName: position.exchangeName ?? undefined,
    dbPositionId: position.tradingPositionId,
    hasDbMatch: true,
    existsOnExchange: Boolean(position.exchangePositionId),
    tradeOrigin: "COPY_TRADE",
  };
}

export const CopyTradeMirroredPositionsDialog: React.FC<
  CopyTradeMirroredPositionsDialogProps
> = ({
  open,
  onOpenChange,
  subscriptionId,
  userId,
  nickname,
  onSelectPosition,
}) => {
  const [tab, setTab] = useState<PositionsTab>("open");

  const { data: positions = [], isLoading, isError } =
    useCopyTradeMirroredPositions(subscriptionId, userId, open);

  const openPositions = useMemo(
    () => positions.filter((p) => p.status === "OPEN"),
    [positions]
  );
  const closedPositions = useMemo(
    () => positions.filter((p) => p.status === "CLOSED"),
    [positions]
  );

  const visible = tab === "open" ? openPositions : closedPositions;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Your positions · ${nickname}`}
      maxWidth="2xl"
      contentClassName="p-0"
    >
      <div className="p-6 space-y-4">
        <TabSelector<PositionsTab>
          variant="compact"
          options={[
            { value: "open", label: `Open (${openPositions.length})` },
            { value: "closed", label: `Closed (${closedPositions.length})` },
          ]}
          value={tab}
          onChange={setTab}
        />

        {isLoading && (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        )}

        {isError && !isLoading && (
          <p className="text-sm text-text-secondary text-center py-8">
            Could not load your mirrored positions.
          </p>
        )}

        {!isLoading && !isError && visible.length === 0 && (
          <p className="text-sm text-text-secondary text-center py-8">
            {tab === "open"
              ? "No open positions mirrored from this trader yet."
              : "No closed mirrored positions yet."}
          </p>
        )}

        {!isLoading && !isError && visible.length > 0 && (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {visible.map((pos) => (
              <MirroredPositionRow
                key={pos.mirrorId}
                position={pos}
                livePriceEnabled={open}
                onClick={() =>
                  onSelectPosition(mirroredViewToDrawerPosition(pos))
                }
              />
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};

const MirroredPositionRow: React.FC<{
  position: CopyTradeMirroredPositionView;
  livePriceEnabled: boolean;
  onClick: () => void;
}> = ({ position, livePriceEnabled, onClick }) => {
  const isOpen = position.status === "OPEN";
  const sideLabel = position.positionType === "LONG" ? "Long" : "Short";
  const exchangeCode = mapExchangeCode(
    position.exchangeCode,
    position.exchangeName
  );

  const { price: liveMarkPrice, isSubscribed } = useTokenLivePrice(
    position.symbol,
    exchangeCode,
    livePriceEnabled && isOpen
  );

  const displayMarkPrice = liveMarkPrice ?? position.markPrice ?? 0;
  const livePnL = useMemo(() => {
    if (!isOpen || !displayMarkPrice) return null;
    return calculateUnrealizedPnL(
      position.entryPrice,
      displayMarkPrice,
      position.usdAmount,
      position.leverage,
      position.positionType
    );
  }, [
    isOpen,
    displayMarkPrice,
    position.entryPrice,
    position.usdAmount,
    position.leverage,
    position.positionType,
  ]);

  const pnl = isOpen
    ? (livePnL?.pnl ?? position.unrealizedPnl)
    : position.realizedPnl;
  const pnlPercentage = isOpen
    ? (livePnL?.pnlPercentage ?? position.pnlPercentage)
    : position.pnlPercentage;

  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left bg-primary/30 rounded-xl p-3 border border-border/50 flex items-center justify-between gap-3 hover:border-accent/40 transition-colors"
    >
      <div className="min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-text-primary">
            {position.symbol}
          </span>
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              position.positionType === "LONG"
                ? "bg-green-500/15 text-green-400"
                : "bg-red-500/15 text-red-400"
            }`}
          >
            {sideLabel}
          </span>
          <span className="text-xs text-text-secondary">
            {position.leverage}x
          </span>
          {position.exchangeName && (
            <span className="text-xs text-text-secondary">
              {position.exchangeName}
            </span>
          )}
        </div>
        <p className="text-xs text-text-secondary flex items-center gap-1 flex-wrap">
          <span>Entry ${formatDecimal(position.entryPrice)}</span>
          {isOpen ? (
            <>
              <span>·</span>
              <span
                className={
                  isSubscribed ? "transition-all duration-150" : undefined
                }
              >
                Mark ${formatDecimal(displayMarkPrice)}
              </span>
              {isSubscribed && (
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-green-500" />
                </span>
              )}
              <span>·</span>
              <span className={pnlColor(pnl)}>{formatSignedUsd(pnl)}</span>
              {isFiniteNumber(pnlPercentage) && (
                <span className={pnlColor(pnlPercentage)}>
                  ({pnlPercentage >= 0 ? "+" : ""}
                  {pnlPercentage.toFixed(2)}%)
                </span>
              )}
            </>
          ) : (
            <span>· Exit ${formatDecimal(position.exitPrice)}</span>
          )}
        </p>
        <p className="text-xs text-text-secondary">
          Opened {formatDateTime(position.openedAt)}
          {!isOpen ? ` · Closed ${formatDateTime(position.closedAt)}` : ""}
        </p>
      </div>
      <div className="text-right shrink-0">
        {!isOpen && (
          <>
            <p className={`text-sm font-medium ${pnlColor(pnl)}`}>
              {formatSignedUsd(pnl)}
            </p>
            {isFiniteNumber(pnlPercentage) && (
              <p className={`text-xs ${pnlColor(pnlPercentage)}`}>
                {pnlPercentage >= 0 ? "+" : ""}
                {pnlPercentage.toFixed(2)}%
              </p>
            )}
          </>
        )}
        <p className="text-xs text-text-secondary">
          ${formatUsd(position.usdAmount)}
        </p>
      </div>
    </button>
  );
};
